import { apiFetch, apiFetchBlob, apiFetchRaw } from './client.js';

/**
 * Admin onboarding API — applicant review queue + versioned policy management.
 * Mirrors apps/api/src/routes/admin/onboarding.ts.
 */

export type ApplicationStatus =
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'DECLINED'
  | 'INFO_REQUESTED';

/**
 * The "under review" list filter (DEV-160): submitted and waiting on a
 * reviewer. Not a stored status; the server resolves it. /kyc-queue redirects
 * to Onboarding with it applied.
 */
export const UNDER_REVIEW_FILTER = 'UNDER_REVIEW';

export type ApplicationStatusFilter = ApplicationStatus | typeof UNDER_REVIEW_FILTER;

export type ApplicationDecision = 'APPROVE' | 'DECLINE' | 'REQUEST_INFO';

export type AccountType = 'PERSONAL' | 'BUSINESS';

export interface ApplicationListEntry {
  id: string;
  customerId: string;
  email: string;
  /** Legal name for a business, the applicant's name for a person. */
  applicantName: string;
  accountType: AccountType;
  country: string;
  jurisdiction: string;
  stageId: string;
  status: ApplicationStatus;
  kycStatus: string;
  /** The customer's AML rating, the one the customer header shows; UNKNOWN when not rated. */
  riskRating: string;
  /** Submitted and waiting on a reviewer: only these have SLA days. */
  underReview: boolean;
  /** When it was last submitted for review; null when not under review. */
  waitingSince: string | null;
  /** Whole days waiting since then; null when not under review. */
  slaDays: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationDetail extends ApplicationListEntry {
  policyVersion: string;
  snapshot: Record<string, unknown>;
  decisionReason: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
}

export type PolicyVersionStatus = 'BUILT_IN' | 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

export interface PolicyVersionMeta {
  id: string | null;
  version: string;
  status: PolicyVersionStatus;
  changeSummary: string;
  createdBy: string;
  createdAt: string;
  activatedAt: string | null;
}

/** The policy JSON is rendered/edited generically — typed loosely on purpose. */
export type PolicyJson = Record<string, unknown>;

export interface PolicyOverview {
  active: PolicyVersionMeta;
  policy: PolicyJson;
  versions: PolicyVersionMeta[];
}

export interface PolicyVersionDetail {
  meta: PolicyVersionMeta;
  policy: PolicyJson;
}

export interface ResolvedStage {
  id: string;
  screen: string;
  phase: string;
  label: string;
}

export interface PlanPreview {
  source: string;
  policyVersion: string;
  plan: {
    criteria: { accountType: AccountType; country: string; jurisdiction: string };
    jurisdiction: { id: string; displayName: string; currency: string; accountIdentifier: string };
    stages: ResolvedStage[];
  };
}

/** A reviewer's disposition of one uploaded file. */
export interface DocumentReview {
  id: string;
  slot: string;
  filename: string;
  sizeBytes: number;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
  /** CUSTOMER_UPLOAD is a submission to judge; PROVIDER_CHECK is check evidence. */
  source: 'CUSTOMER_UPLOAD' | 'STAFF_UPLOAD' | 'PROVIDER_CHECK';
  partyProviderId: string | null;
  partyName: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  /** Optional. A document without dates simply is not aged. */
  issuedAt?: string | null;
  expiresAt?: string | null;
  /** The uploaded name, when a reviewer has since renamed it. */
  originalFilename?: string | null;
  /** Only an administrator is shown removed files; these say so. */
  removedAt?: string | null;
  removeReason?: string | null;
  removeKind?: string | null;
  createdAt: string;
}

/** One unmet condition standing between this application and approval. */
export interface ApprovalBlocker {
  code: 'DOCUMENTS_PENDING' | 'PARTIES_MISSING' | 'IDENTITY_PENDING' | 'IDENTITY_UNKNOWN';
  detail: string;
}

/**
 * A non-blocking condition shown at approval (DEV-114): a profile with no
 * usable sanctions screening result. Approval stays possible and is recorded
 * as made without one.
 */
export interface ApprovalWarning {
  code: 'SCREENING';
  kind: 'NO_RESULT' | 'FAILED' | 'RUNNING' | 'MATCH_NOT_REVIEWED' | 'UNREADABLE';
  /** Who: "Company Acme Ltd", "Director Jane Doe", "Applicant". */
  profile: string;
  /** What, in plain words: "no sanctions screening result", … */
  detail: string;
}

export interface ApprovalReadiness {
  ready: boolean;
  blockers: ApprovalBlocker[];
  /** Optional so an older server without it still reads as "no warnings". */
  warnings?: ApprovalWarning[];
  documents: { total: number; verified: number; pending: number; rejected: number };
  parties: { total: number; approved: number; outstanding: string[]; readable: boolean };
  accountType: string | null;
}

export interface ApplicationReviewState {
  documents: DocumentReview[];
  readiness: ApprovalReadiness;
}

export interface ApplicationListFilters {
  status?: ApplicationStatusFilter;
  accountType?: AccountType;
  country?: string;
  /** Matches the applicant's name or e-mail. */
  search?: string;
  sort?: 'updated' | 'created';
  direction?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ApplicationPage {
  entries: ApplicationListEntry[];
  total: number;
  page: number;
  limit: number;
  hasNext: boolean;
}

function toQuery(filters: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const str = qs.toString();
  return str === '' ? '' : `?${str}`;
}

export const onboardingApi = {
  /** One page of the queue, with the total so the pager knows where it ends. */
  listApplications: async (filters: ApplicationListFilters = {}): Promise<ApplicationPage> => {
    const envelope = await apiFetchRaw<ApplicationListEntry[]>(
      `/admin/onboarding/applications${toQuery(filters as Record<string, unknown>)}`,
    );
    const entries = envelope.data ?? [];
    return {
      entries,
      total: envelope.meta?.total ?? entries.length,
      page: envelope.meta?.page ?? 1,
      limit: envelope.meta?.limit ?? entries.length,
      hasNext: envelope.meta?.hasNext ?? false,
    };
  },

  getApplication: (id: string): Promise<ApplicationDetail> =>
    apiFetch<ApplicationDetail>(`/admin/onboarding/applications/${id}`),

  /** Fetch the raw bytes of an uploaded onboarding document (for preview/download). */
  fetchDocumentBlob: (applicationId: string, docId: string): Promise<Blob> =>
    apiFetchBlob(`/admin/onboarding/applications/${applicationId}/documents/${docId}`),

  decide: (
    id: string,
    decision: ApplicationDecision,
    reason: string,
  ): Promise<ApplicationDetail> =>
    apiFetch<ApplicationDetail>(`/admin/onboarding/applications/${id}/decision`, {
      method: 'POST',
      body: { decision, reason },
    }),

  /** Agent edits, each change audited server-side (admin, field, old -> new, reason). */
  editApplication: (
    id: string,
    changes: { path: string; value: string | number | boolean }[],
    reason: string,
  ): Promise<ApplicationDetail> =>
    apiFetch<ApplicationDetail>(`/admin/onboarding/applications/${id}/snapshot`, {
      method: 'PATCH',
      body: { changes, reason },
    }),

  /** The change history for one application, newest first. */
  applicationEdits: (
    id: string,
  ): Promise<
    {
      id: string;
      adminId: string;
      fieldPath: string;
      oldValue: unknown;
      newValue: unknown;
      reason: string;
      createdAt: string;
    }[]
  > => apiFetch(`/admin/onboarding/applications/${id}/edits`),

  /**
   * Every decision taken on this application — approve, decline, request-info —
   * with the reason the reviewer gave and who gave it. Append-only: a later
   * decision never erases an earlier reason, which is what the tracker item
   * about "my comment disappeared" was about.
   */
  applicationHistory: (
    id: string,
  ): Promise<
    {
      id: string;
      summary: string;
      reason: string | null;
      decidedBy: string;
      decidedAt: string;
      decision: string | null;
    }[]
  > => apiFetch(`/admin/onboarding/applications/${id}/history`),

  /**
   * What the reviewer needs before deciding: every document's disposition and
   * the checklist of conditions that gate Approve server-side.
   */
  reviewState: (id: string): Promise<ApplicationReviewState> =>
    apiFetch<ApplicationReviewState>(`/admin/onboarding/applications/${id}/review-state`),

  /** Approve or reject one uploaded document. A rejection must say why. */
  decideDocument: (
    id: string,
    docId: string,
    decision: 'VERIFY' | 'REJECT',
    note?: string,
  ): Promise<DocumentReview> =>
    apiFetch<DocumentReview>(`/admin/onboarding/applications/${id}/documents/${docId}`, {
      method: 'PATCH',
      body: { decision, ...(note !== undefined && note !== '' ? { note } : {}) },
    }),

  /** Take a document off the file. Kept, and recorded with who and why. */
  removeDocument: (id: string, docId: string, kind: string, reason: string): Promise<DocumentReview> =>
    apiFetch<DocumentReview>(`/admin/onboarding/applications/${id}/documents/${docId}`, {
      method: 'DELETE',
      body: { kind, reason },
    }),

  /** Rename without uploading again. The uploaded name is kept in the history. */
  renameDocument: (id: string, docId: string, name: string): Promise<DocumentReview> =>
    apiFetch<DocumentReview>(`/admin/onboarding/applications/${id}/documents/${docId}/name`, {
      method: 'PATCH',
      body: { name },
    }),

  /** Record the dates a document turns on. Optional; null clears one. */
  recordDocumentDates: (
    id: string,
    docId: string,
    dates: { issuedAt: string | null; expiresAt: string | null },
  ): Promise<DocumentReview> =>
    apiFetch<DocumentReview>(`/admin/onboarding/applications/${id}/documents/${docId}/dates`, {
      method: 'PUT',
      body: dates,
    }),

  getPolicy: (): Promise<PolicyOverview> =>
    apiFetch<PolicyOverview>('/admin/onboarding/policy'),

  getPolicyVersion: (id: string): Promise<PolicyVersionDetail> =>
    apiFetch<PolicyVersionDetail>(`/admin/onboarding/policy/versions/${id}`),

  createPolicyVersion: (input: {
    policy: PolicyJson;
    changeSummary: string;
    activate: boolean;
  }): Promise<PolicyVersionMeta> =>
    apiFetch<PolicyVersionMeta>('/admin/onboarding/policy/versions', {
      method: 'POST',
      body: input,
    }),

  activatePolicyVersion: (id: string): Promise<PolicyVersionMeta> =>
    apiFetch<PolicyVersionMeta>(`/admin/onboarding/policy/versions/${id}/activate`, {
      method: 'POST',
      body: {},
    }),

  previewPlan: (input: {
    accountType: AccountType;
    country?: string;
    policy?: PolicyJson;
    versionId?: string;
  }): Promise<PlanPreview> =>
    apiFetch<PlanPreview>('/admin/onboarding/policy/preview', {
      method: 'POST',
      body: input,
    }),
};
